import React, { useState, useEffect, useCallback } from 'react';
import API from '../api';
import { ShieldPlus } from 'lucide-react';

// stesso azzurro della card "Documenti del fascicolo" (ACCENTO in CaricaDocumenti.js)
const AZZURRO = '#2B6CB0';
const AZZURRO_CHIARO = '#E6EFF8';

export default function RuoliPermessi() {
  const [ruoli, setRuoli] = useState([]);
  const [permessi, setPermessi] = useState([]);
  const [matrice, setMatrice] = useState({});
  const [messaggio, setMessaggio] = useState(null);
  const [mostraNuovo, setMostraNuovo] = useState(false);
  const [nuovoRuolo, setNuovoRuolo] = useState({ nome: '', descrizione: '' });

  const carica = useCallback(() => {
    API.get('/ruoli').then(r => setRuoli(r.data)).catch(() => {});
    API.get('/permessi').then(r => setPermessi(r.data)).catch(() => {});
    API.get('/ruoli-permessi').then(r => setMatrice(r.data)).catch(() => {});
  }, []);

  useEffect(() => { carica(); }, [carica]);

  const notifica = (testo, errore = false) => {
    setMessaggio({ testo, errore });
    setTimeout(() => setMessaggio(null), 4000);
  };

  const haP = (ruoloId, permessoId) =>
    (matrice[String(ruoloId)] || []).includes(permessoId);

  const toggle = async (ruoloId, permessoId) => {
    const nuovoStato = !haP(ruoloId, permessoId);
    try {
      await API.patch(`/ruoli/${ruoloId}/permessi/${permessoId}?attivo=${nuovoStato}`);
      setMatrice(prev => {
        const m = { ...prev };
        const lista = new Set(m[String(ruoloId)] || []);
        nuovoStato ? lista.add(permessoId) : lista.delete(permessoId);
        m[String(ruoloId)] = [...lista];
        return m;
      });
    } catch (e) {
      notifica(e.response?.data?.detail || 'Errore', true);
    }
  };

  const creaRuolo = async () => {
    if (!nuovoRuolo.nome.trim()) {
      notifica('Nome ruolo obbligatorio', true);
      return;
    }
    try {
      await API.post('/ruoli', nuovoRuolo);
      notifica('Ruolo creato');
      setNuovoRuolo({ nome: '', descrizione: '' });
      setMostraNuovo(false);
      carica();
    } catch (e) {
      notifica(e.response?.data?.detail || 'Errore', true);
    }
  };

  // Raggruppa i permessi per sezione (prefisso prima del punto)
  const gruppi = permessi.reduce((acc, p) => {
    const sezione = p.codice.split('.')[0];
    (acc[sezione] = acc[sezione] || []).push(p);
    return acc;
  }, {});

  // intestazione delle colonne fissa durante lo scroll; l'ombra fa da bordo (i bordi non restano fissi)
  const thFisso = {
    padding: '10px 14px', position: 'sticky', top: 0, zIndex: 1, background: '#fafaf9',
    boxShadow: 'inset 0 -1px 0 #e7e5e4'
  };

  const inputStyle = {
    padding: '8px 10px', border: '1px solid #d6d3d1', borderRadius: 6,
    fontSize: 13, boxSizing: 'border-box'
  };

  return (
    <div style={{ padding: 24, maxWidth: 1100 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h2 style={{ margin: 0 }}>Ruoli e permessi</h2>
        <button onClick={() => setMostraNuovo(!mostraNuovo)}
          style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '9px 16px',
                   background: '#1c1917', color: 'white', border: 'none', borderRadius: 8,
                   fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
          <ShieldPlus size={16} /> Nuovo ruolo
        </button>
      </div>

      {messaggio && (
        <div style={{
          padding: '10px 14px', borderRadius: 8, marginBottom: 16, fontSize: 13,
          background: messaggio.errore ? '#FEF2F2' : '#F0FDF4',
          border: `1px solid ${messaggio.errore ? '#FECACA' : '#BBF7D0'}`,
          color: messaggio.errore ? '#B91C1C' : '#166534'
        }}>
          {messaggio.testo}
        </div>
      )}

      {mostraNuovo && (
        <div style={{ background: 'white', border: '1px solid #e7e5e4', borderRadius: 10,
                      padding: 20, marginBottom: 20, display: 'flex', gap: 12, alignItems: 'flex-end' }}>
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, display: 'block', marginBottom: 4 }}>Nome *</label>
            <input style={inputStyle} value={nuovoRuolo.nome} placeholder="es. praticante"
              onChange={e => setNuovoRuolo({ ...nuovoRuolo, nome: e.target.value })} />
          </div>
          <div style={{ flex: 1 }}>
            <label style={{ fontSize: 12, fontWeight: 600, display: 'block', marginBottom: 4 }}>Descrizione</label>
            <input style={{ ...inputStyle, width: '100%' }} value={nuovoRuolo.descrizione}
              onChange={e => setNuovoRuolo({ ...nuovoRuolo, descrizione: e.target.value })} />
          </div>
          <button onClick={creaRuolo}
            style={{ padding: '9px 20px', background: '#1c1917', color: 'white', border: 'none',
                     borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
            Crea
          </button>
        </div>
      )}

      {/* altezza massima: la tabella scorre dentro la card, così l'intestazione delle colonne resta in alto */}
      <div style={{ background: 'white', border: '1px solid #e7e5e4', borderRadius: 10, overflow: 'auto',
                    maxHeight: 'calc(100vh - 160px)' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: '#fafaf9' }}>
              <th style={{ ...thFisso, textAlign: 'left', minWidth: 220 }}>Permesso</th>
              {ruoli.map(r => (
                <th key={r.id} style={{ ...thFisso, textAlign: 'center',
                                        textTransform: 'capitalize', whiteSpace: 'nowrap' }}
                    title={r.descrizione}>
                  {r.nome.replace('_', ' ')}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {Object.entries(gruppi).map(([sezione, lista], i) => (
              <React.Fragment key={sezione}>
                {i > 0 && (
                  <tr aria-hidden="true">
                    <td colSpan={1 + ruoli.length} style={{ height: 10, padding: 0 }} />
                  </tr>
                )}
                <tr style={{ background: AZZURRO_CHIARO }}>
                  <td colSpan={1 + ruoli.length}
                      style={{ padding: '10px 14px', fontWeight: 700, fontSize: 12, borderLeft: `4px solid ${AZZURRO}`,
                               textTransform: 'uppercase', letterSpacing: 1, color: AZZURRO }}>
                    {sezione}
                  </td>
                </tr>
                {lista.map(p => (
                  <tr key={p.id} style={{ borderTop: '1px solid #f5f5f4' }}>
                    <td style={{ padding: '8px 14px' }}>
                      <div style={{ fontWeight: 600 }}>{p.descrizione}</div>
                      <div style={{ fontFamily: 'monospace', fontSize: 11, color: '#a8a29e' }}>{p.codice}</div>
                    </td>
                    {ruoli.map(r => (
                      <td key={r.id} style={{ textAlign: 'center', padding: '8px 14px' }}>
                        <input type="checkbox" checked={haP(r.id, p.id)}
                          onChange={() => toggle(r.id, p.id)}
                          style={{ width: 16, height: 16, cursor: 'pointer', accentColor: '#1c1917' }} />
                      </td>
                    ))}
                  </tr>
                ))}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      <div style={{ marginTop: 12, fontSize: 12, color: '#78716c' }}>
        Le modifiche ai permessi hanno effetto entro un minuto, senza bisogno di un nuovo login.
      </div>
    </div>
  );
}
